import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box, Button, Typography, IconButton, Paper, CircularProgress } from '@mui/material';
import { Upload, Delete, Crop } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { vortxColors } from '@vortx/shared-ui';

export default function ScreenshotCapture({ onScreenshotChange, onCaptureStart, isDark }) {
    const [screenshot, setScreenshot] = useState(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [cropStart, setCropStart] = useState(null);
    const [cropEnd, setCropEnd] = useState(null);
    const [fullCapture, setFullCapture] = useState(null);
    const fileInputRef = useRef(null);
    const overlayRef = useRef(null);

    // Handle paste events
    useEffect(() => {
        const handlePaste = (e) => {
            console.log('[Screenshot] Paste event detected');
            const items = e.clipboardData?.items;
            if (!items) {
                console.log('[Screenshot] No clipboard items found');
                return;
            }

            console.log(`[Screenshot] Clipboard has ${items.length} items`);
            for (const item of items) {
                console.log(`[Screenshot] Clipboard item type: ${item.type}`);
                if (item.type.startsWith('image/')) {
                    console.log('[Screenshot] Found image in clipboard, processing...');
                    const file = item.getAsFile();
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        console.log(`[Screenshot] Pasted image loaded, size: ${event.target.result.length} bytes`);
                        setScreenshot(event.target.result);
                        onScreenshotChange(event.target.result);
                    };
                    reader.onerror = (err) => {
                        console.error('[Screenshot] Failed to read pasted image:', err);
                    };
                    reader.readAsDataURL(file);
                    break;
                }
            }
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [onScreenshotChange]);

    const handleCapture = async () => {
        console.log('[Screenshot] Starting capture...');

        // Notify parent to hide dialog before capturing
        if (onCaptureStart) {
            console.log('[Screenshot] Notifying parent to hide dialog');
            onCaptureStart();
        }

        setIsCapturing(true);

        // Longer delay to let React re-render and hide the dialog
        console.log('[Screenshot] Waiting 300ms for dialog to hide...');
        await new Promise(resolve => setTimeout(resolve, 300));

        try {
            console.log('[Screenshot] Starting html2canvas capture...');
            const startTime = Date.now();

            // Capture the entire page
            // Skip elements that cause html2canvas issues:
            // - Animated canvas elements (e.g., RetroWaveform)
            // - Elements with CSS 3D transforms (e.g., RetroWireframeGlobe)
            const canvas = await html2canvas(document.body, {
                logging: false,
                useCORS: true,
                allowTaint: true,
                onclone: (clonedDoc) => {
                    const canvasCount = clonedDoc.querySelectorAll('canvas').length;
                    console.log(`[Screenshot] Removing ${canvasCount} canvas elements from clone`);
                    clonedDoc.querySelectorAll('canvas').forEach(el => el.remove());

                    // Remove elements with 3D transforms (not supported by html2canvas)
                    let transform3dCount = 0;
                    clonedDoc.querySelectorAll('*').forEach(el => {
                        const style = window.getComputedStyle(el);
                        if (style.transformStyle === 'preserve-3d' || style.perspective !== 'none') {
                            transform3dCount++;
                            el.remove();
                        }
                    });
                    console.log(`[Screenshot] Removed ${transform3dCount} elements with 3D transforms`);

                    // Remove elements explicitly marked as non-capturable
                    const ignoreCount = clonedDoc.querySelectorAll('[data-screenshot-ignore="true"]').length;
                    console.log(`[Screenshot] Removing ${ignoreCount} elements marked as non-capturable`);
                    clonedDoc.querySelectorAll('[data-screenshot-ignore="true"]').forEach(el => el.remove());
                }
            });

            const duration = Date.now() - startTime;
            console.log(`[Screenshot] html2canvas completed in ${duration}ms`);
            console.log(`[Screenshot] Canvas size: ${canvas.width}x${canvas.height}`);

            const dataUrl = canvas.toDataURL('image/png');
            console.log(`[Screenshot] Data URL length: ${dataUrl.length} bytes`);

            setFullCapture(dataUrl);
            console.log('[Screenshot] Full capture set, cropping overlay should now appear');
        } catch (err) {
            console.error('[Screenshot] Capture failed:', err);
            console.error('[Screenshot] Error stack:', err.stack);
            setIsCapturing(false);
            // Notify parent to restore popover visibility
            onScreenshotChange(null);
            alert(`Screenshot capture failed: ${err.message}\n\nPlease try the Upload option instead.`);
        }
    };

    const handleCropMouseDown = (e) => {
        console.log('[Screenshot] Mouse down for crop selection');
        if (!fullCapture) {
            console.warn('[Screenshot] No fullCapture available, ignoring mouse down');
            return;
        }
        const rect = overlayRef.current.getBoundingClientRect();
        const start = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        console.log(`[Screenshot] Crop start position: (${start.x}, ${start.y})`);
        setCropStart(start);
        setCropEnd(null);
    };

    const handleCropMouseMove = (e) => {
        if (!cropStart || !fullCapture) return;
        const rect = overlayRef.current.getBoundingClientRect();
        setCropEnd({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
    };

    const handleCropMouseUp = () => {
        console.log('[Screenshot] Mouse up - finalizing crop selection');
        if (!cropStart || !cropEnd || !fullCapture) {
            console.warn('[Screenshot] Missing data for crop:', {
                hasCropStart: !!cropStart,
                hasCropEnd: !!cropEnd,
                hasFullCapture: !!fullCapture
            });
            return;
        }

        // Calculate crop region
        const x = Math.min(cropStart.x, cropEnd.x);
        const y = Math.min(cropStart.y, cropEnd.y);
        const width = Math.abs(cropEnd.x - cropStart.x);
        const height = Math.abs(cropEnd.y - cropStart.y);

        console.log(`[Screenshot] Crop region: x=${x}, y=${y}, width=${width}, height=${height}`);

        if (width < 10 || height < 10) {
            console.warn(`[Screenshot] Crop region too small (${width}x${height}), minimum is 10x10. Selection discarded.`);
            alert('Selection too small. Please drag to create a larger selection area.');
            setCropStart(null);
            setCropEnd(null);
            return;
        }

        console.log('[Screenshot] Creating cropped image...');

        // Create cropped image
        const img = new Image();
        img.onload = () => {
            console.log(`[Screenshot] Image loaded: ${img.width}x${img.height}`);
            const canvas = document.createElement('canvas');
            const scaleX = img.width / overlayRef.current.clientWidth;
            const scaleY = img.height / overlayRef.current.clientHeight;

            console.log(`[Screenshot] Scale factors: scaleX=${scaleX}, scaleY=${scaleY}`);

            canvas.width = width * scaleX;
            canvas.height = height * scaleY;

            console.log(`[Screenshot] Cropped canvas size: ${canvas.width}x${canvas.height}`);

            const ctx = canvas.getContext('2d');
            ctx.drawImage(
                img,
                x * scaleX, y * scaleY,
                width * scaleX, height * scaleY,
                0, 0,
                canvas.width, canvas.height
            );

            const croppedDataUrl = canvas.toDataURL('image/png');
            console.log(`[Screenshot] Cropped image created, size: ${croppedDataUrl.length} bytes`);

            setScreenshot(croppedDataUrl);
            onScreenshotChange(croppedDataUrl);
            setFullCapture(null);
            setIsCapturing(false);
            setCropStart(null);
            setCropEnd(null);

            console.log('[Screenshot] Crop complete, overlay closed');
        };
        img.onerror = (err) => {
            console.error('[Screenshot] Failed to load image for cropping:', err);
            alert('Failed to process screenshot. Please try again or use the Upload option.');
            handleCancelCapture();
        };
        img.src = fullCapture;
    };

    const handleCancelCapture = () => {
        console.log('[Screenshot] Capture cancelled by user');
        setFullCapture(null);
        setIsCapturing(false);
        setCropStart(null);
        setCropEnd(null);
        // Notify parent to restore popover visibility
        onScreenshotChange(null);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            console.log('[Screenshot] No file selected');
            return;
        }

        console.log(`[Screenshot] File selected: ${file.name}, size: ${file.size} bytes, type: ${file.type}`);

        const reader = new FileReader();
        reader.onload = (event) => {
            console.log(`[Screenshot] File loaded, data URL size: ${event.target.result.length} bytes`);
            setScreenshot(event.target.result);
            onScreenshotChange(event.target.result);
        };
        reader.onerror = (err) => {
            console.error('[Screenshot] Failed to read uploaded file:', err);
            alert('Failed to read the uploaded file. Please try again.');
        };
        reader.readAsDataURL(file);
    };

    const handleRemove = () => {
        console.log('[Screenshot] Removing screenshot');
        setScreenshot(null);
        onScreenshotChange(null);
    };

    // Crop overlay modal - uses Portal to render outside Popover hierarchy
    if (fullCapture) {
        return ReactDOM.createPortal(
            <Box
                ref={overlayRef}
                onMouseDown={handleCropMouseDown}
                onMouseMove={handleCropMouseMove}
                onMouseUp={handleCropMouseUp}
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 10001,  // Above the hidden Popover
                    cursor: 'crosshair',
                    backgroundImage: `url(${fullCapture})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'top left'
                }}
            >
                {/* Dark overlay */}
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0,0,0,0.5)'
                }} />

                {/* Selection rectangle */}
                {cropStart && cropEnd && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: Math.min(cropStart.x, cropEnd.x),
                            top: Math.min(cropStart.y, cropEnd.y),
                            width: Math.abs(cropEnd.x - cropStart.x),
                            height: Math.abs(cropEnd.y - cropStart.y),
                            border: `2px solid ${vortxColors.gold}`,
                            bgcolor: 'transparent',
                            boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)'
                        }}
                    />
                )}

                {/* Instructions */}
                <Paper sx={{
                    position: 'absolute',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    p: 2,
                    bgcolor: vortxColors.charcoal,
                    color: vortxColors.white
                }}>
                    <Typography>Drag to select the area with the bug</Typography>
                    <Button
                        size="small"
                        onClick={handleCancelCapture}
                        sx={{ mt: 1, color: vortxColors.gold }}
                    >
                        Cancel
                    </Button>
                </Paper>
            </Box>,
            document.body
        );
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: isDark ? vortxColors.stone : 'text.secondary' }}>
                Screenshot *
            </Typography>

            {screenshot ? (
                <Box sx={{ position: 'relative' }}>
                    <img
                        src={screenshot}
                        alt="Bug screenshot"
                        style={{
                            maxWidth: '100%',
                            maxHeight: 200,
                            borderRadius: 4,
                            border: `1px solid ${isDark ? vortxColors.gold : vortxColors.stone}`
                        }}
                    />
                    <IconButton
                        size="small"
                        onClick={handleRemove}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                        }}
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                        variant="outlined"
                        startIcon={isCapturing ? <CircularProgress size={16} /> : <Crop />}
                        onClick={handleCapture}
                        size="small"
                        disabled={isCapturing}
                        sx={{ borderColor: vortxColors.gold, color: vortxColors.gold }}
                    >
                        {isCapturing ? 'Capturing...' : 'Capture Area'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<Upload />}
                        onClick={() => fileInputRef.current?.click()}
                        size="small"
                        disabled={isCapturing}
                        sx={{ borderColor: vortxColors.stone, color: isDark ? vortxColors.stone : 'text.secondary' }}
                    >
                        Upload
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                    />
                    <Typography variant="caption" sx={{ width: '100%', color: isDark ? vortxColors.stone : 'text.secondary' }}>
                        {isCapturing ? 'Capturing screen, please wait...' : 'or paste (Ctrl/Cmd+V)'}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
